import React from "react";
import { IBusinessCreditReportResponse, BusinessCreditReportStatePII } from "../../../../store/businessCreditReportService/types";
import { toggle } from "../../../../util/common";
import { useTranslation } from "react-i18next";

interface IProps {
    bCreditReportData: IBusinessCreditReportResponse;
    reportObject: BusinessCreditReportStatePII;
    showAsAccordion?: boolean;
}
const CreditRatingScoreAndLimit: React.FunctionComponent<IProps> = (props) => {
    const showPreviousReport = props.bCreditReportData?.uiData?.previousDate ? true : false;
    const { t: tLang, i18n: i18nLang } = useTranslation();

    return (
        <React.Fragment>
            <div role="listitem" className="data-row">
                <div className="link-item">
                    {props.showAsAccordion ? (
                        <button
                            type="button"
                            aria-expanded="false"
                            className="link-button"
                            id="btn-credit-score-limit"
                            onClick={(e) => toggle("btn-credit-score-limit-div", "btn-credit-score-limit")}
                        >
                            <span className="row link-item">
                                <span className="col-xs col-no-gutter link-text">
                                    <span className="link-title">
                                        <span className="btn-toggle-status sr-only">Show</span> Credit Score and Limit
                                    </span>
                                </span>
                            </span>
                        </button>
                    ) : (
                        <h5 className="report-sub-title">Credit Score and Limit</h5>
                    )}

                    <div
                        className="wallet-details"
                        id="btn-credit-score-limit-div"
                        style={{ ...(props.showAsAccordion ? { display: "none" } : {}) }}
                        aria-hidden="true"
                    >
                        <div role="grid" className="data-grid row-height-xs " aria-labelledby="btn-credit-score-limit">
                            <div role="row" className="data-row condensed row">
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="score-limit"></div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="score-limit-current">
                                        Current ({props.bCreditReportData?.uiData?.currentDate})
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    {showPreviousReport && (
                                        <div className="data-label" id="score-limit-previous">
                                            Previous ({props.bCreditReportData?.uiData?.previousDate})
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div role="row" className="data-row condensed row">
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-label" aria-labelledby="credit-score">
                                        {tLang("dashboard.creditScore.title")}
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-value" aria-labelledby="score-limit-current">
                                        {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_score}
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    {showPreviousReport && (
                                        <div className="data-value" aria-labelledby="score-limit-previous">
                                            {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.previous_score}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div role="row" className="data-row condensed row">
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="score-rating">
                                        Score Rating
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-value" aria-labelledby="score-rating">
                                        {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_rating}
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    {showPreviousReport && (
                                        <div className="data-value" aria-labelledby="score-rating">
                                            {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.previous_rating}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div role="row" className="data-row condensed row">
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="risk-rating">
                                        Risk Rating
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-value" aria-labelledby="risk-rating">
                                        {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_risk_rating}
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    {showPreviousReport && (
                                        <div className="data-value" aria-labelledby="risk-rating">
                                            {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.previous_risk_rating}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div role="row" className="data-row condensed row">
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-label" id="recommanded-credit-limit">
                                        Recommended Credit Limit
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    <div className="data-value" aria-labelledby="recommanded-credit-limit">
                                        {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.current_recommended_credit_limit}
                                    </div>
                                </div>
                                <div role="gridcell" className="col-xs-4 col-sm-4 col-no-gutter">
                                    {showPreviousReport && (
                                        <div className="data-value" aria-labelledby="recommanded-credit-limit">
                                            {props.reportObject?.businessCompanyReport?.credit_score_and_limit?.previous_recommended_credit_limit}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default CreditRatingScoreAndLimit;
